import { PageHeader } from "@/components/StatCard";
import { Stat, Section, Loading, Empty, Pill, useFetch } from "./_shared";

const THEME_LABEL = {
  profile: "Profil",
  offering: "Angebot",
  circle: "Kreis",
  availability: "Verfügbarkeit",
  payments: "Zahlungen",
  post: "Beitrag",
  method: "Methode",
  weekly_plan: "Wochenplan",
  calendar: "Kalender",
  discover: "Entdecken",
};

function rateTone(pct) {
  if (pct >= 70) return "good";
  if (pct >= 40) return "warn";
  return "bad";
}

export default function GuideAnalytics() {
  const { data, loading } = useFetch("/admin/guide/analytics");
  if (loading) return <Loading />;

  const rows = data?.per_theme || [];
  const worst = data?.biggest_dropoff_theme;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Admin"
        title="Onboarding-Guide"
        subtitle="Wie Coaches den Einrichtungs-Pfad durchlaufen — Abschlussquoten, Abbrüche & Dauer."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Coaches mit Guide" value={data?.coaches_with_guide ?? 0} testId="admin-guide-total" />
        <Stat label="Gestartet" value={data?.coaches_started ?? 0} />
        <Stat
          label="Komplett abgeschlossen"
          value={data?.coaches_completed_all ?? 0}
          tone={(data?.coaches_completed_all ?? 0) > 0 ? "good" : "default"}
        />
        <Stat
          label="Ø Dauer"
          value={data?.avg_time_to_complete_min != null ? `${data.avg_time_to_complete_min} min` : "—"}
        />
      </div>

      {worst && (
        <Section title="Größter Abbruchpunkt" testId="admin-guide-dropoff">
          <p className="text-sm text-muted-foreground">
            Die meisten Coaches steigen bei{" "}
            <span className="font-semibold text-foreground">{THEME_LABEL[worst] || worst}</span> aus. Hier lohnt es sich,
            den Schritt noch einfacher oder einladender zu gestalten.
          </p>
        </Section>
      )}

      <Section title="Pro Thema" testId="admin-guide-per-theme">
        {rows.length === 0 ? (
          <Empty text="Noch keine Guide-Daten vorhanden." />
        ) : (
          <div className="space-y-2">
            <div className="hidden grid-cols-12 gap-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 md:grid">
              <span className="col-span-3">Thema</span>
              <span className="col-span-2 text-right">Geöffnet</span>
              <span className="col-span-2 text-right">Erledigt</span>
              <span className="col-span-2 text-right">Übersprungen</span>
              <span className="col-span-3 text-right">Abschlussquote</span>
            </div>
            {rows.map((r) => (
              <div
                key={r.theme}
                data-testid={`admin-guide-row-${r.theme}`}
                className="grid grid-cols-2 items-center gap-2 rounded-xl border border-border bg-background p-3 md:grid-cols-12"
              >
                <span className="col-span-2 text-sm font-medium text-foreground md:col-span-3">
                  {THEME_LABEL[r.theme] || r.theme}
                </span>
                <span className="text-xs text-muted-foreground md:col-span-2 md:text-right">
                  <span className="md:hidden">Geöffnet: </span>{r.opened}
                </span>
                <span className="text-xs text-muted-foreground md:col-span-2 md:text-right">
                  <span className="md:hidden">Erledigt: </span>{r.completed}
                </span>
                <span className="text-xs text-muted-foreground md:col-span-2 md:text-right">
                  <span className="md:hidden">Übersprungen: </span>{r.skipped}
                </span>
                <span className="col-span-2 flex items-center justify-end gap-2 md:col-span-3">
                  <Pill tone={rateTone(r.completion_rate_pct)}>{r.completion_rate_pct}%</Pill>
                  {r.drop_off > 0 && <span className="text-[11px] text-rose-600">−{r.drop_off} Abbruch</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
